import type { FiltersState } from "../../../common/hooks/useFilters";
import { AvailableModel, ThinkingModeEffortLevel } from "../../../services/claude";
import { ArtifactType } from "../../../types/artifact";

export type RowSelectionMode = "selected" | "all";

export type InteractionFilterFields = Pick<
  FiltersState,
  "timeRange" | "sentiment" | "relevance" | "risk" | "protocol" | "user" | "processDecision" | "callType" | "search" | "scoreRange"
>;

export interface InteractionFilter extends InteractionFilterFields {
  rowSelectionMode: RowSelectionMode;
  includedArtifactTypes: ArtifactType[];
}

export interface ContextFilter extends InteractionFilter {
  artifactCount: number;
  totalTokens: number;
}

export type AnswerMode = "quick" | "deep" | "none";

export type DatasetPreset = "summaries" | "transcripts";

export const DATASET_PRESET_TYPES: Record<DatasetPreset, ArtifactType[]> = {
  summaries: [ArtifactType.Summary],
  transcripts: [ArtifactType.Summary, ArtifactType.Transcript]
};

export const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  [ArtifactType.Summary]: "Summaries",
  [ArtifactType.Transcript]: "Transcripts",
  [ArtifactType.Scorecard]: "Scorecards",
  [ArtifactType.CallDataRecord]: "Call Data Records"
};

export interface PendingChunkedQuery {
  content: string;
  temperature: number;
  chunks: DataChunk[];
  systemMessageId: string;
}

export interface ChatMessage {
  id: string;
  content: string;
  role: "user" | "assistant" | "error" | "system";
  timestamp: Date;
  temperature?: number;
  isStreaming?: boolean;
  isThinking?: boolean;
  thinkingContent?: string;
  exceedsTokenLimit?: boolean;
  contextData?: ContextDataItem[] | null;
  chunkProgress?: {
    current: number;
    total: number;
    isComplete: boolean;
  };
  chunkResponses?: string[];
  chunks?: DataChunk[];
  contextFilter?: ContextFilter;
  includedArtifactTypes?: ArtifactType[];
  answerMode?: AnswerMode;
}

export interface MessageHistory {
  role: "user" | "assistant";
  content: string;
}

export type ContextDataItem = Record<string, unknown> & {
  index: number;
  interactionId: string;
};

export interface DataChunk {
  data: ContextDataItem[];
  startIndex: number;
  endIndex: number;
  estimatedTokens: number;
  actualTokens?: number;
}

export interface ChatState {
  // Messages
  messages: ChatMessage[];
  messageHistory: MessageHistory[];

  // UI State
  inputValue: string;
  editingMessage: string | null;
  isDrawerOpen: boolean;
  loading: boolean;

  // Data State
  contextData: ContextDataItem[] | null;
  isContextLoaded: boolean;
  includedArtifactTypes: ArtifactType[];
  totalTokens: number;
  pendingQuery: PendingChunkedQuery | null;
  permittedEmails: string[] | null;
  tenantId: string | null;

  // Filter change detection
  lastSelectedContext: { interactionIds: string[]; filter: InteractionFilterFields | null };

  // Token limit computed properties
  exceedsTokenLimit: boolean;
  isBlockedByTokenLimit: boolean;

  // Token length state for optimization
  totalCalculatedTokens: number;
  singleContextTokenLength: number;

  // Chunking State
  isChunkingEnabled: boolean;
  contextChunks: DataChunk[];
  continueChunkedQuery: (() => Promise<void>) | null;
  isCreatingChunks: boolean;

  // Query Execution State
  isExecuting: boolean;
  currentAbortController: AbortController | null;

  // User Preferences (integrated into Zustand store)
  temperature: number;
  thinkingMode: boolean;
  maxTokens: number;
  budgetTokens: number;
  maxInputTokens: number;
  model: AvailableModel;
  effort: ThinkingModeEffortLevel;
  answerMode: AnswerMode;
  lastInjectedAnswerMode: AnswerMode | null;

  // Feature Flags
  thinkingModeEnabled: boolean;

  // Previous session
  viewingPreviousSession: boolean;
  previousSessionMessages: ChatMessage[];
  hasSavedPreviousSession: boolean;
}

export interface CreateDataChunksOptions {
  query?: string;
  abortSignal?: AbortSignal;
}

export interface ChatActions {
  // Message actions
  addMessage: (message: ChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addToHistory: (entry: MessageHistory) => void;
  resetMessages: () => void;
  showChunkConfirmation: (params: { content: string; temperature: number }) => Promise<void>;
  sendChunkedMessage: () => Promise<void>;
  startChunkingProcess: (params: { content: string; temperature: number }) => Promise<void>;

  setInputValue: (value: string) => void;
  setEditingMessage: (messageId: string | null) => void;
  setDrawerOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;

  // Previous session actions
  saveAndRefreshSession: () => void;
  showPreviousSession: () => void;
  hidePreviousSession: () => void;

  // Data actions
  setContextData: (data: ContextDataItem[] | null) => void;
  setIncludedArtifactTypes: (types: ArtifactType[]) => void;
  setTotalTokens: (tokens: number) => void;
  setPendingQuery: (query: PendingChunkedQuery | null) => void;
  setPermittedEmails: (emails: string[] | null) => void;
  setTenantId: (tenantId: string | null) => void;

  // Token length actions
  setTotalCalculatedTokens: (tokens: number) => void;
  setSingleContextTokenLength: (tokens: number) => void;

  // Chunking actions
  enableChunking: () => Promise<void>;
  clearTokenLimitMessages: () => void;
  createDataChunks: (options: CreateDataChunksOptions) => Promise<DataChunk[]>;
  setCreatingChunks: (creating: boolean) => void;

  // Data Loader actions
  loadContext: (interactionIds: string[], keepExistingMessages?: boolean, interactionFilter?: InteractionFilter) => Promise<void>;
  resetContext: () => void;
  calculateTokens: () => Promise<number>;

  // Query Execution actions
  setExecuting: (executing: boolean) => void;
  stopGeneration: () => void;
  setAbortController: (controller: AbortController | null) => void;

  // Combined actions
  resetChat: () => void;
  handleEditMessage: (message: ChatMessage) => void;
  truncateMessagesAfter: (messageId: string) => void;
  cancelEditMessage: () => void;
  sendMessage: (content: string, customTemperature?: number) => Promise<void>;

  // User Preferences actions
  setTemperature: (value: number) => void;
  setThinkingMode: (value: boolean) => void;
  setMaxTokens: (value: number) => void;
  setBudgetTokens: (value: number) => void;
  setMaxInputTokens: (value: number) => void;
  setModel: (value: AvailableModel) => void;
  setEffort: (value: ThinkingModeEffortLevel) => void;
  setAnswerMode: (mode: AnswerMode) => void;
  setDatasetPreset: (preset: DatasetPreset) => void;
  loadPreferencesFromStorage: () => void;

  // Feature Flag actions
  setThinkingModeEnabled: (enabled: boolean) => void;

  // Streaming actions
  handleStreamUpdate: (content: string) => void;
  handleThinkingDelta: (delta: string, type: string) => void;
  handleComplete: () => void;
}

export type ChatStore = ChatState & ChatActions;

export type ChatStateUpdater = (state: ChatStore) => Partial<ChatState>;
export type ChatSetState = (partial: Partial<ChatState> | ChatStateUpdater) => void;
export type ChatGetState = () => ChatStore;
